import React, { useState, useRef } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import { useVariantSelectionState } from '../context/VariantSelectionContext';
import type {
    LibraryImage,
    LibraryVideo,
    LibraryDiagram,
} from '../type/product-gallery-types';
import type Variant from '@modules/product/type/variant';
import { useLocation } from '@remix-run/react';

const getActiveIndex = (
    stack: Array<LibraryImage | LibraryVideo | LibraryDiagram>,
    selectedImageID?: number,
    previousImageID?: number,
) => {
    if (!selectedImageID || selectedImageID === previousImageID) {
        return null;
    }

    const matchingIndex = stack.findIndex(
        (item) => item.ID === selectedImageID,
    );
    return matchingIndex !== -1 ? matchingIndex : null;
};

const useGalleryVariantSelect = (
    stack: Array<LibraryImage | LibraryVideo | LibraryDiagram>,
) => {
    const { selectedVariant } = useVariantSelectionState();
    const prevRef = useRef<Variant | null>(null);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const initialSKU = searchParams.get('sku');

    const initialIndex =
        initialSKU && selectedVariant?.SKU === initialSKU
            ? (getActiveIndex(stack, selectedVariant?.Image?.ID) ?? 0)
            : 0;

    const [slideIndex, setSlideIndex] = useState(initialIndex ?? 0);
    const hasMountedRef = useRef(false);

    useDeepCompareEffect(() => {
        prevRef.current = null;
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        setSlideIndex(0);
    }, [stack]);

    React.useEffect(() => {
        if (!selectedVariant) {
            return;
        }

        const prevVariant = prevRef.current;

        if (!prevVariant) {
            prevRef.current = selectedVariant;
            setSlideIndex(initialIndex);
            return;
        }

        if (prevVariant.ID === selectedVariant.ID) {
            return;
        }

        const hasProductChanged =
            prevVariant.Product.ID !== selectedVariant.Product.ID;

        prevRef.current = selectedVariant;

        const activeIndex = hasProductChanged
            ? 0
            : getActiveIndex(
                  stack,
                  selectedVariant.Image?.ID,
                  prevVariant?.Image?.ID,
              );

        if (activeIndex !== null) {
            setSlideIndex(activeIndex);
        }
    }, [initialIndex, selectedVariant, stack]);

    return {
        slideIndex,
    };
};

export default useGalleryVariantSelect;
